<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import {
  CATEGORY_FIELDS,
  MISMATCH_DATA_TYPE,
  OPTIONS_FIELD,
  PRODUCT_TYPE,
  UNIQUE_KEY_FIELD_KEYS,
} from "@/util/constants";
import {
  getCustomObjectNamePlural,
  isCustomObjectField,
  isPrimaryField,
  isTrueCustomObjectField,
} from "@/util/customObjectUtils";
import { AlignmentPosition, applyStyles } from "@/util/methods";
// @ts-ignore
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  Edit01Icon,
  Monitor04Icon,
  Phone01Icon,
  PlusIcon,
  Trash01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
// @ts-ignore
import RichTextEditor from "@/components/text-editor/index.vue";
import { getDeviceAware, setDeviceAware } from "@/composition/useDeviceAware";
import { deviceOptions } from "@/util/constants";
import {
  UIAlert,
  UIButton,
  UICheckbox,
  UICollapse,
  UICollapseItem,
  UIDivider,
  UIDropdown,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UISwitch,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { snakeCase } from "lodash";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Card from "../common/Card.vue";
import CodeEditorModel from "../common/CodeEditorModel.vue";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CountInput from "../common/CountInput.vue";
import DateSettings from "../common/DateSettings.vue";
import DropdownEditor from "../common/DropdownEditor.vue";
import EditOpportunitySettingsModal from "../common/EditOpportunitySettingsModal.vue";
import RatingEditor from "../common/ElementEditors/RatingEditor.vue";
import LabelAlignment from "../common/Elements/LabelAlignment.vue";
import Switch from "../common/Elements/Switch.vue";
import FileUploadEditor from "../common/FileUploadEditor.vue";
import ListEditor from "../common/ListEditor.vue";
import RadioSelectEditor from "../common/RadioSelectEditor.vue";
import ScoreEditor from "../common/ScoreEditor.vue";
import TextSidebar from "../common/TextSidebar.vue";

const store = useAppStore();
const emitter = useEmitter();

const { t } = useI18n();
const { getCustomFieldsFromStore } = storeToRefs(store);
const props = defineProps({
  selectedElement: {
    type: Object,
    default() {
      return {};
    },
  },
});

const isMobile = computed(() => store.deviceMode === "mobile");

const { slideIndex, elementIndex } = store.getSlideIndexAndElementIndex(props.selectedElement);
//Get Slide Settings or Slide Elements Options Settings
const selectedElementStore = reactive(
  props.selectedElement.slideId && props.selectedElement.elementId
    ? store.slides[slideIndex].slideData[elementIndex]
    : store.slides[slideIndex]
);

const tncElement = store.slides[slideIndex].slideData[elementIndex];

const dateElement = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex];
});

const showModal = ref(false);
const uniqueKeyPlaceholder = ref(snakeCase(selectedElementStore?.label));
const uniqueKeyError = ref(false);
const customFieldError = ref(false);

const showModelUpdate = (modalUpdate: boolean) => {
  showModal.value = modalUpdate;
};

const submitUpdate = (updatedCode: string) => {
  selectedElementStore.html = updatedCode;
  store.anyUnsavedChanges = true;
};

const onClickOfEditHTML = () => {
  showModal.value = !showModal.value;
};

const onBack = () => {
  store.isAnyElementActive = false;
};

const switchDeviceMode = (option: string) => {
  store.deviceMode = option as any;
};

const getSlideName = computed(() => {
  return store.slides[slideIndex].slideName;
});

const getLabel = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].label;
});

const getTypeLabel = computed(() => {
  const typeLabel = store.slides[slideIndex].slideData[elementIndex]?.typeLabel;
  if (typeLabel) {
    return typeLabel;
  }
  const model = selectedElementStore?.model;
  if (model && model !== "contact") {
    let displayName = "";
    if (model === "business") {
      displayName = t("common.business");
    } else if (model === "opportunity") {
      displayName = t("common.opportunity");
    } else if (isTrueCustomObjectField(model)) {
      displayName = getCustomObjectNamePlural(model, store.customObjects || []);
    }
    if (!displayName) {
      const seg = model.split(".").pop() || model;
      displayName = seg.charAt(0).toUpperCase() + seg.slice(1);
    }
    if (typeof displayName === "string" && displayName.length > 0) {
      displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
    }
    const prefix = t("common.object") || "Object";
    return `${prefix}: ${displayName}`;
  }
  return (
    (selectedElementStore.tag === "header" ? "Text" : selectedElementStore.label) ||
    selectedElementStore.name ||
    ""
  );
});

const getUniqueKey = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex]?.fieldKey;
});

const updatePicklistOptions = (options) => {
  if (CATEGORY_FIELDS.includes(store.slides[slideIndex].slideData[elementIndex]?.dataType)) {
    store.slides[slideIndex].slideData[elementIndex].picklistOptions = options.map(
      (option) => option.label
    );
  } else {
    store.slides[slideIndex].slideData[elementIndex].picklistOptions = options;
  }
  if (store.slides[slideIndex].slideData[elementIndex]?.id) {
    store.slides[slideIndex].slideData[elementIndex].customfieldUpdated = true;
  }
  store.anyUnsavedChanges = true;
};

const updateStyle = (style) => {
  if (selectedElementStore) {
    selectedElementStore.style = style;
    store.anyUnsavedChanges = true;
  }
};

const updateUniqueKey = (value: string) => {
  if (value.trim() !== "") {
    store.slides[slideIndex].slideData[elementIndex].fieldKey = value;
    store.anyUnsavedChanges = true;
    uniqueKeyError.value = checkUniqueKeyExists(value, slideIndex, elementIndex);
  }
};

const getPlaceholder = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].placeholder;
});

const getShortLabel = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].shortLabel;
});

const getHiddenQuery = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].hiddenFieldQueryKey;
});

const getRequired = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].required;
});

const getHidden = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].hidden;
});

const getEnableCountryPicker = computed(() => {
  if (store.slides[slideIndex].slideData[elementIndex].enableCountryPicker === undefined) {
    return true;
  }
  return store.slides[slideIndex].slideData[elementIndex].enableCountryPicker;
});

const enableValidateEmailModel = computed({
  get() {
    return store.slides[slideIndex].slideData[elementIndex]?.enableValidateEmail ?? false;
  },
  set(value: boolean) {
    store.slides[slideIndex].slideData[elementIndex].enableValidateEmail = value;
    store.anyUnsavedChanges = true;
  },
});

const enableValidatePhoneModel = computed({
  get() {
    return store.slides[slideIndex].slideData[elementIndex]?.enableValidatePhone ?? false;
  },
  set(value: boolean) {
    store.slides[slideIndex].slideData[elementIndex].enableValidatePhone = value;
    store.anyUnsavedChanges = true;
  },
});

const getHiddenValue = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].hiddenFieldValue;
});

const getSource = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].value;
});

const getSpreadColumns = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex]?.spreadColumns;
});
const getNumberOfColumns = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex]?.columnsNumber;
});

const getDefaultNumberOfColumns = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex]?.picklistOptionsImage ? undefined : 1;
});

const isHiddenFieldDisable = computed(() => {
  const element = store.slides[slideIndex].slideData[elementIndex];
  if (!element) {
    return false;
  }
  if (element?.required) {
    return true;
  }
  const primaryInfo = isPrimaryField(element, store.customObjects || []);
  return Boolean(primaryInfo?.isPrimary);
});

const isRequiredFieldDisable = computed(() =>
  Boolean(store.slides[slideIndex].slideData[elementIndex]?.hidden)
);

// Disable Required checkbox only when field is hidden or a primary field
const isRequiredCheckboxDisabled = computed(() => {
  const element = store.slides[slideIndex].slideData[elementIndex];
  const baseDisabled = isRequiredFieldDisable.value; // disabled when hidden
  if (!element) {
    return baseDisabled;
  }
  // Lock required state for primary fields of company/custom objects; allow toggling otherwise
  const primaryInfo = isPrimaryField(element, store.customObjects || []);
  const isPrimary = Boolean(primaryInfo?.isPrimary);
  return baseDisabled || isPrimary;
});

const questionOptions = computed(() => {
  const currentElementUuid = selectedElementStore.uuid;
  return store.getQuizQuestionDropdown(currentElementUuid);
});

const disableUniqueKey = computed(() => {
  const el = store.slides[slideIndex]?.slideData?.[elementIndex];
  return (
    el?.customEdited ||
    !el?.custom ||
    el?.dataType === "SCORE" ||
    el?.dataType === MISMATCH_DATA_TYPE.RATING
  );
});

const disableCustomFieldName = computed(() => {
  const el = store.slides[slideIndex]?.slideData?.[elementIndex];
  return el?.customEdited || !el?.custom;
});
const checkUniqueKeyExists = (
  key: string,
  currentSlideIndex?: number,
  currentElementIndex?: number
) => {
  // Check if the field is standard
  if (store.slides[currentSlideIndex]?.slideData[currentElementIndex]?.standard) {
    return false;
  }
  // Check in store.customFields
  const isUniqueExistsInCustomFields = store.customFields.some((customField) => {
    const uniqueKey = customField.fieldKey?.split(".")?.[1];
    return uniqueKey === key || customField.fieldKey === key;
  });

  // Check in all slides and their elements
  const isUniqueExistsInSlides = store.slides.some((slide, slideIndex) => {
    if (!slide.slideData) {
      return false;
    }

    return slide.slideData.some((element, elementIndex) => {
      // Skip current element if indices are provided
      if (
        currentSlideIndex !== undefined &&
        currentElementIndex !== undefined &&
        slideIndex === currentSlideIndex &&
        elementIndex === currentElementIndex
      ) {
        return false;
      }
      return element.fieldKey === key;
    });
  });

  return isUniqueExistsInCustomFields || isUniqueExistsInSlides;
};

const checkInitialValidation = () => {
  if (!selectedElementStore?.id) {
    const currentFieldKey = selectedElementStore?.fieldKey;

    if (currentFieldKey) {
      // Check if fieldKey exists in other elements
      uniqueKeyError.value = currentFieldKey
        ? checkUniqueKeyExists(currentFieldKey, slideIndex, elementIndex)
        : false;
    }

    const currentCustomFieldName = selectedElementStore?.name || selectedElementStore?.label;

    customFieldError.value = checkUniqueCustomFieldNameExists(
      currentCustomFieldName,
      slideIndex,
      elementIndex
    );
  }
};

const checkUniqueCustomFieldNameExists = (
  name: string,
  currentSlideIndex?: number,
  currentElementIndex?: number
) => {
  // Check if the field is standard
  if (store.slides[currentSlideIndex]?.slideData[currentElementIndex]?.standard) {
    return false;
  }
  const isCustomFieldNameExistsInCustomFields = store.customFields.some((customField) => {
    return customField.name === name || customField.label === name;
  });

  const isCustomFieldNameExistsInSlides = store.slides.some((slide, slideIndex) => {
    if (!slide.slideData) {
      return false;
    }
    return slide.slideData.some((element, elementIndex) => {
      if (
        currentSlideIndex !== undefined &&
        currentElementIndex !== undefined &&
        slideIndex === currentSlideIndex &&
        elementIndex === currentElementIndex
      ) {
        return false;
      }
      return element.name === name || element.label === name;
    });
  });

  return isCustomFieldNameExistsInCustomFields || isCustomFieldNameExistsInSlides;
};

onMounted(() => {
  checkInitialValidation();
});

// Sidebar state management via global event bus
const getSidebarState = (callback: (state: Record<string, boolean>) => void): void => {
  if (typeof callback === "function") {
    callback({ quizElementSidebarOpen: store.isAnyElementActive && !!store.quizId });
  }
};

/**
 * Direct getter for this sidebar's open state.
 */
const getQuizElementSidebarState = (callback: (state: boolean) => void): void => {
  if (typeof callback === "function") {
    callback(store.isAnyElementActive && !!store.quizId);
  }
};

/**
 * Close the Quiz Element Sidebar (and any others) when a global close event is received.
 */
const closeAllSidebars = (): void => {
  if (store.isAnyElementActive) {
    store.isAnyElementActive = false;
  }
};

/**
 * Restore sidebar state from a saved snapshot.
 */
const restoreSidebarState = (state: Record<string, boolean>): void => {
  if (state && typeof state.quizElementSidebarOpen === "boolean") {
    store.isAnyElementActive = state.quizElementSidebarOpen;
  }
};

// -----------------------------------------------------------------------------
// Sidebar Event Lifecycle Hooks
// -----------------------------------------------------------------------------

onBeforeMount(() => {
  emitter.on("get-sidebar-state", getSidebarState);
  emitter.on("get-quiz-element-sidebar-state", getQuizElementSidebarState);
  emitter.on("close-all-sidebars", closeAllSidebars);
  emitter.on("restore-sidebar-state", restoreSidebarState);
  emitter.on("logic:update", (receviedLogic) => {
    if (store.logic[receviedLogic.current]) {
      store.logic[receviedLogic.current][receviedLogic.index] = receviedLogic;
    } else {
      store.logic[receviedLogic.current] = {};
      store.logic[receviedLogic.current][receviedLogic.index] = receviedLogic;
    }
    store.anyUnsavedChanges = true;
  });
  emitter.on("logic:sort", (receviedLogic) => {
    if (store.logic[receviedLogic.current]) {
      delete store.logic[receviedLogic.current][receviedLogic.newIndex];
      delete store.logic[receviedLogic.current][receviedLogic.oldIndex];
    }
    store.anyUnsavedChanges = true;
  });
  emitter.on("category:update", (category) => {
    if (store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory) {
      store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory[
        category.index
      ] = category;
    } else {
      store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory = {};
      store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory[
        category.index
      ] = category;
    }
    store.anyUnsavedChanges = true;
  });
  emitter.on("category:sort", (category) => {
    if (store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory) {
      delete store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory[
        category.oldIndex
      ];
      delete store.slides[category.slideIndex].slideData[category.elementIndex].scoreByCategory[
        category.newIndex
      ];
      forceRenderOptionFields();
    }
    store.anyUnsavedChanges = true;
  });
  emitter.on("category:rerender", () => {
    forceRenderOptionFields();
    store.anyUnsavedChanges = true;
  });
  emitter.on("option:deleted", (deleteLogic) => {
    if (store.logic[deleteLogic.current]) {
      delete store.logic[deleteLogic.current];
    }
    if (store.slides[deleteLogic.slideIndex].slideData[deleteLogic.elementIndex].scoreByCategory) {
      delete store.slides[deleteLogic.slideIndex].slideData[deleteLogic.elementIndex]
        .scoreByCategory;
    }
    store.anyUnsavedChanges = true;
  });
});
const optionModified = ref(0);

const optionModifierKey = computed(() => String(optionModified.value));

const forceRenderOptionFields = () => {
  optionModified.value += 1;
};

onBeforeUnmount(() => {
  emitter.off("get-sidebar-state", getSidebarState);
  emitter.off("get-quiz-element-sidebar-state", getQuizElementSidebarState);
  emitter.off("close-all-sidebars", closeAllSidebars);
  emitter.off("restore-sidebar-state", restoreSidebarState);
  emitter.off("logic:update");
  emitter.off("logic:sort");
  emitter.off("category:update");
  emitter.off("category:sort");
  emitter.off("category:rerender");
  emitter.off("option:deleted");
});

const handleImageWidth = (val) => {
  store.slides[slideIndex].slideData[elementIndex].width = val;
  store.anyUnsavedChanges = true;
};

const handleImageHeight = (val) => {
  store.slides[slideIndex].slideData[elementIndex].height = val;
  store.anyUnsavedChanges = true;
};

const handleTextColor = (val: string) => {
  store.slides[slideIndex].slideData[elementIndex].textColor = val;
  store.anyUnsavedChanges = true;
};

const handleLinkColor = (val: string) => {
  store.slides[slideIndex].slideData[elementIndex].linkColor = val;
  store.anyUnsavedChanges = true;
};

const getAltImage = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex].alt;
});

const alignImage = (align) => {
  store.slides[slideIndex].slideData[elementIndex].align = align;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};

const updatePreview = (element) => {
  const content = element.placeholder2
    ? `${element.placeholder};${element.placeholder2}`
    : `${element.placeholder}`;
  element.preview = applyStyles(content, element.linkColor, element.textColor);
};

const updateCalculation = (calculation) => {
  store.slides[slideIndex].slideData[elementIndex].calculation = calculation;
  store.anyUnsavedChanges = true;
};

const saveOpportunitySettings = (settings) => {
  store.opportunitySettings = settings;
  store.anyUnsavedChanges = true;
  store.showOpportunitiesModal = false;
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

watch(
  () => [
    tncElement?.placeholder,
    tncElement?.mobilePlaceholder,
    tncElement?.placeholder2,
    tncElement?.linkColor,
    tncElement?.textColor,
  ],
  () => {
    updatePreview(tncElement);
  }
);

//To Maintain Reactivity of Store
const handleTextUpdate = (output) => {
  Object.assign(store.slides[slideIndex].slideData[elementIndex], output);
};

const updateSlideName = (value: string) => {
  store.slides[slideIndex].slideName = value;
  store.anyUnsavedChanges = true;
};

const updateLabel = (value: string) => {
  uniqueKeyPlaceholder.value = snakeCase(value);
  if (
    selectedElementStore?.dataType === "SCORE" &&
    !uniqueKeyPlaceholder.value.startsWith("score_")
  ) {
    uniqueKeyPlaceholder.value = "score_" + uniqueKeyPlaceholder.value;
  }
  if (
    selectedElementStore?.dataType === MISMATCH_DATA_TYPE.RATING &&
    !uniqueKeyPlaceholder.value.startsWith("rating_" + UNIQUE_KEY_FIELD_KEYS.RATING)
  ) {
    uniqueKeyPlaceholder.value =
      "rating_" + UNIQUE_KEY_FIELD_KEYS.RATING + "_" + uniqueKeyPlaceholder.value;
  }
  if (!selectedElementStore?.id) {
    updateUniqueKey(uniqueKeyPlaceholder.value);
    customFieldError.value = checkUniqueCustomFieldNameExists(value, slideIndex, elementIndex);
  }
  store.slides[slideIndex].slideData[elementIndex].label = value;
  store.anyUnsavedChanges = true;
};

const handleLabelAlignment = (value: AlignmentPosition) => {
  store.slides[slideIndex].slideData[elementIndex].labelAlignment = value;
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].placeholder = value;
  store.anyUnsavedChanges = true;
};

const getPlaceholder2 = computed(() => {
  return store.slides[slideIndex].slideData[elementIndex]?.placeholder2;
});

const updatePlaceholder2 = (value: string): void => {
  store.slides[slideIndex].slideData[elementIndex].placeholder2 = value;
  store.anyUnsavedChanges = true;
};

const addSecondTnc = () => {
  if (!store.slides[slideIndex].slideData[elementIndex].placeholder2) {
    store.slides[slideIndex].slideData[elementIndex].placeholder2 = " ";
    store.anyUnsavedChanges = true;
  }
};

const removeSecondTnc = () => {
  if (store.slides[slideIndex].slideData[elementIndex].placeholder2) {
    store.slides[slideIndex].slideData[elementIndex].placeholder2 = undefined;
    store.anyUnsavedChanges = true;
  }
};

const updateShortLabel = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].shortLabel = value;
  store.anyUnsavedChanges = true;
};

const updateHiddenQuery = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].hiddenFieldQueryKey = value;
  store.anyUnsavedChanges = true;
};

const updateRequired = (value: boolean) => {
  store.slides[slideIndex].slideData[elementIndex].required = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateHidden = (value: boolean) => {
  store.slides[slideIndex].slideData[elementIndex].hidden = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateEnableCountryPicker = (value: boolean) => {
  store.slides[slideIndex].slideData[elementIndex].enableCountryPicker = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateHiddenValue = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].hiddenFieldValue = value;
  store.anyUnsavedChanges = true;
};

const updateSource = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].value = value;
  store.anyUnsavedChanges = true;
};

const updateAltImage = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].alt = value;
  store.anyUnsavedChanges = true;
};

const updateDateFormat = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].format = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};
const updateDateSeparator = (value: string) => {
  store.slides[slideIndex].slideData[elementIndex].separator = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateDatedisablePicker = (value: boolean) => {
  store.slides[slideIndex].slideData[elementIndex].disablePicker = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateSpreadColumns = (value: boolean) => {
  store.slides[slideIndex].slideData[elementIndex].spreadColumns = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};
const updateNumberOfColumns = (value: number) => {
  store.slides[slideIndex].slideData[elementIndex].columnsNumber = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const handleRatingConfig = (key, value) => {
  store.slides[slideIndex].slideData[elementIndex][key] = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const expandedCollapseItems = ref(["Question", "Options"]);

const handleCollapseChange = (panel) => {
  const newExpandedItems = [...expandedCollapseItems.value];
  if (panel.expanded) {
    if (!newExpandedItems.includes(panel.name)) {
      newExpandedItems.push(panel.name);
    }
  } else {
    const index = newExpandedItems.indexOf(panel.name);
    if (index !== -1) {
      newExpandedItems.splice(index, 1);
    }
  }
  expandedCollapseItems.value = newExpandedItems;
};
</script>

<template>
  <section id="quizContainerSettings" class="flex flex-col gap-2">
    <div class="relative mt-2 flex items-center justify-center">
      <div class="arrow-button absolute left-0 ml-4" @click="onBack">
        <ArrowLeftIcon class="h-4 w-4 text-gray-800" />
      </div>
      <div class="text-md text-gray-900">{{ getTypeLabel }}</div>
      <div v-if="selectedElement.elementId === undefined" class="text-md text-gray-900">
        {{ selectedElementStore.slideName }}
      </div>
    </div>
    <div id="quizSettings">
      <div v-if="selectedElement.elementId === undefined" class="flex flex-col p-3">
        <div class="form-group">
          <div class="flex space-x-3">
            <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
              t("quiz.pageName")
            }}</span>
          </div>
          <UIInput
            id="pageName"
            class="standard-input-ghl"
            :model-value="getSlideName"
            @update:model-value="updateSlideName"
            @blur="store.addToHistory()"
          >
          </UIInput>
        </div>
        <div class="form-group">
          <div class="flex space-x-3">
            <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
              t("quiz.pagePosition")
            }}</span>
          </div>
          <UIInputNumber
            id="pagePosition"
            :value="slideIndex + 1"
            :disabled="true"
            size="large"
            :show-button="false"
            class="standard-input-ghl"
            @blur="store.addToHistory()"
          ></UIInputNumber>
        </div>
      </div>
      <UICollapse
        id="question"
        :arrow-placement="'left'"
        :default-expanded-names="expandedCollapseItems"
        :expanded-names="expandedCollapseItems"
        @item-header-click="handleCollapseChange"
      >
        <RatingEditor
          v-if="selectedElementStore?.type === 'rating'"
          :product="store.product"
          :rating-element="selectedElementStore"
          :index="selectedElement.index"
          :is-required-field-disable="isRequiredCheckboxDisabled"
          :is-hidden-field-disable="isHiddenFieldDisable"
          :is-form="true"
          @update-label="updateLabel"
          @update-short-label="updateShortLabel"
          @update-required="updateRequired"
          @update-rating-count="handleRatingConfig('count', $event)"
          @update-icon-selected-state="handleRatingConfig('color', $event)"
          @update-lowest-rating="handleRatingConfig('lowestRating', $event)"
          @update-highest-rating="handleRatingConfig('highestRating', $event)"
          @update-icon-selected="handleRatingConfig('iconType', $event)"
          @update-icon-alignment="handleRatingConfig('iconAlignment', $event)"
          @update-calculation-type="handleRatingConfig('calculationType', $event)"
          @update-icon-unselected-state="handleRatingConfig('inactiveColor', $event)"
          @update-label-alignment="handleLabelAlignment"
        ></RatingEditor>
        <UICollapseItem
          v-else-if="selectedElement.elementId !== undefined"
          id="general-settings"
          :title="t('common.generalSettings')"
          name="Question"
        >
          <template #header>
            <div class="flex w-full items-center justify-between">
              <div>{{ t("common.generalSettings") }}</div>
              <div
                v-if="['header', 'terms_and_conditions'].includes(selectedElementStore?.tag)"
                @click.stop
              >
                <UIDropdown
                  id="general-device-toggle"
                  :options="deviceOptions"
                  @select="switchDeviceMode"
                >
                  <template #default>
                    <div class="flex items-center gap-0.5">
                      <Phone01Icon
                        v-if="store.deviceMode === 'mobile'"
                        class="h-4 w-4 cursor-pointer"
                      />
                      <Monitor04Icon v-else class="h-4 w-4 cursor-pointer" />
                      <ChevronDownIcon class="h-3 w-3" />
                    </div>
                  </template>
                </UIDropdown>
              </div>
            </div>
          </template>
          <div v-if="selectedElementStore?.type == 'html'">
            <div class="form-group">
              <Card :header="selectedElementStore.placeholder">
                <template #default>
                  <UIButton
                    id="editHtml"
                    type="default"
                    class="w-[100%]"
                    @click="onClickOfEditHTML"
                    >{{ t("common.editHTML") }}</UIButton
                  >
                </template>
              </Card>
            </div>
            <div>
              <CodeEditorModel
                v-if="showModal"
                :model-active="showModal"
                :code="store.slides[slideIndex].slideData[elementIndex].html"
                @update:show-model="showModelUpdate"
                @update:submit="submitUpdate"
              />
            </div>
          </div>
          <div v-else-if="selectedElementStore.tag === 'header'">
            <TextSidebar
              :property="
                JSON.parse(JSON.stringify(store.slides[slideIndex].slideData[elementIndex]))
              "
              @update:text="handleTextUpdate"
            />
          </div>
          <div v-else-if="selectedElementStore.tag === 'source'">
            <h6 class="field-name-sidebar">{{ selectedElementStore.label }}</h6>
            <div class="form-group">
              <UIInput
                id="sourceLabel"
                class="standard-input-ghl"
                :placeholder="t('common.enterSourceValue')"
                :model-value="getSource"
                @update:model-value="updateSource"
                @blur="store.addToHistory()"
              ></UIInput>
            </div>
          </div>
          <div v-else-if="selectedElementStore.tag === 'captcha'">
            <div class="form-group">
              <div class="flex space-x-3">
                <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.label")
                }}</span>
              </div>
              <UIInput
                id="captchaLabel"
                class="standard-input-ghl"
                :model-value="getLabel"
                @update:model-value="updateLabel"
                @blur="store.addToHistory()"
              >
              </UIInput>
            </div>
            <div class="form-group">
              <LabelAlignment
                :is-element-level="true"
                :label-alignment="selectedElementStore.labelAlignment"
                @update:label-alignment="handleLabelAlignment"
              ></LabelAlignment>
            </div>
          </div>
          <div v-else-if="selectedElementStore.tag === 'image'">
            <h6 class="field-name-sidebar">
              {{ selectedElementStore.label }}
            </h6>
            <div class="form-group">
              <div class="flex space-x-3">
                <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.altText")
                }}</span>
              </div>
              <UIInput
                id="altText"
                class="standard-input-ghl"
                :model-value="getAltImage"
                @update:model-value="updateAltImage"
                @blur="store.addToHistory()"
              ></UIInput>
            </div>
            <div class="form-group">
              <div class="flex space-x-3">
                <div class="alignment">
                  <div class="alignment-div">{{ t("common.alignment") }}</div>
                  <div class="icons-collection">
                    <AlignLeftIcon
                      class="custom-icons h-5 w-5"
                      @click="alignImage('left')"
                    ></AlignLeftIcon>
                    <AlignCenterIcon
                      class="custom-icons h-5 w-5"
                      @click="alignImage('center')"
                    ></AlignCenterIcon>
                    <AlignRightIcon
                      class="custom-icons h-5 w-5"
                      @click="alignImage('right')"
                    ></AlignRightIcon>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group">
              <div class="flex space-x-3">
                <CountInput
                  :label="t('common.imageWidth')"
                  :value="selectedElementStore.width || ''"
                  :max="1800"
                  @update:value="handleImageWidth"
                />
              </div>
            </div>
            <div class="form-group">
              <div class="flex space-x-3">
                <CountInput
                  :label="t('common.imageHeight')"
                  :value="selectedElementStore.height || ''"
                  :max="1800"
                  @update:value="handleImageHeight"
                />
              </div>
            </div>
          </div>
          <div v-else-if="selectedElementStore.tag === 'terms_and_conditions'">
            <div class="form-group flex flex-col gap-y-4">
              <div>
                <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.tnc1")
                }}</span>
                <div>
                  <!-- First TNC Editor with device-aware support -->
                  <div class="mb-4">
                    <RichTextEditor
                      :is-submit-message="true"
                      :model-value="
                        getDeviceAware(
                          selectedElementStore,
                          'placeholder',
                          'mobilePlaceholder',
                          isMobile
                        )
                      "
                      @update:model-value="
                        (value) =>
                          setDeviceAware(
                            selectedElementStore,
                            'placeholder',
                            'mobilePlaceholder',
                            value,
                            isMobile
                          )
                      "
                    />
                  </div>

                  <!-- Add/Remove TNC buttons -->
                  <div class="mb-4 flex justify-end space-x-2">
                    <UIButton
                      v-if="!selectedElementStore?.placeholder2"
                      id="add-second-tnc"
                      type="default"
                      size="small"
                      @click="addSecondTnc"
                    >
                      <PlusIcon class="mr-1.5 h-5 w-5" /> {{ t("common.addAnotherTnc") }}
                    </UIButton>
                  </div>

                  <!-- Second TNC Editor -->
                  <div v-if="selectedElementStore?.placeholder2" class="mb-1">
                    <div class="flex items-center justify-between">
                      <span class="hl-text-input-label block text-sm font-medium text-gray-700">
                        {{ t("common.tnc2") }}
                      </span>
                      <UIButton
                        id="remove-second-tnc-inline"
                        type="danger"
                        size="small"
                        @click="removeSecondTnc"
                      >
                        <Trash01Icon class="h-5 w-5" />
                      </UIButton>
                    </div>
                  </div>
                  <div v-if="selectedElementStore?.placeholder2" class="mb-4">
                    <RichTextEditor
                      :is-submit-message="true"
                      :model-value="getPlaceholder2"
                      @update:model-value="updatePlaceholder2"
                    />
                  </div>
                </div>
              </div>
              <div>
                <div class="flex space-x-3">
                  <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                    {{ t("common.queryKey") }}
                    <UITooltip trigger="hover" placement="top">
                      <template #trigger>
                        <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                      </template>
                      {{ t("common.querykeyTooltipMessage") }}
                    </UITooltip>
                  </div>
                </div>
                <div>
                  <UIInput
                    id="fieldQueryKey"
                    class="standard-input-ghl"
                    :model-value="getHiddenQuery"
                    @update:model-value="updateHiddenQuery"
                    @blur="store.addToHistory()"
                  ></UIInput>
                </div>
              </div>
              <div class="form-check-req-hid">
                <UICheckbox
                  id="fieldRequired"
                  :checked="getRequired"
                  @update:checked="updateRequired"
                  >{{ t("common.required") }}</UICheckbox
                >
              </div>
            </div>
          </div>

          <div v-else-if="['date'].includes(dateElement.type)">
            <DateSettings
              :date-element="dateElement"
              :is-required-field-disable="isRequiredCheckboxDisabled"
              :is-hidden-field-disable="isHiddenFieldDisable"
              :is-custom-field-error="customFieldError"
              @update-label="updateLabel"
              @update-placeholder="updatePlaceholder"
              @update-short-label="updateShortLabel"
              @update-hidden-query="updateHiddenQuery"
              @update-required="updateRequired"
              @update-hidden="updateHidden"
              @update-hidden-value="updateHiddenValue"
              @update-date-format="updateDateFormat"
              @update-date-separator="updateDateSeparator"
              @update-date-disable-picker="updateDatedisablePicker"
              @update-label-alignment="handleLabelAlignment"
            ></DateSettings>
            <div v-if="selectedElementStore?.model === 'opportunity'">
              <UIDivider />
              <div class="m-4 flex justify-center">
                <UIButton
                  id="edit-opportunity-settings"
                  type="tertiary"
                  @click="store.showOpportunitiesModal = true"
                >
                  <Edit01Icon class="mr-2 h-4 w-4" /> {{ t("common.editOpportunitySettings") }}
                </UIButton>
              </div>
            </div>
            <div v-if="isTrueCustomObjectField(selectedElementStore?.model)">
              <UIDivider />
              <div class="m-4 mb-3 flex justify-center">
                <UIButton
                  id="edit-custom-object-association"
                  type="tertiary"
                  @click="
                    store.currentCustomObjectModel = selectedElementStore?.model;
                    store.showContactAssociationModal = true;
                  "
                >
                  <Edit01Icon class="mr-2 h-4 w-4" />
                  {{
                    t("common.editAssociationButton", {
                      edit: t("common.edit"),
                      objectName: getCustomObjectNamePlural(
                        selectedElementStore?.model,
                        store.customObjects || []
                      ),
                      association: t("common.association"),
                    })
                  }}
                </UIButton>
              </div>
            </div>

            <EditOpportunitySettingsModal
              :show="store.showOpportunitiesModal"
              @update:success="saveOpportunitySettings"
              @update:cancel="store.showOpportunitiesModal = false"
            />
            <ContactAssociationSettingsModal
              :show="store.showContactAssociationModal"
              :object-model="store.currentCustomObjectModel"
              @update:success="saveContactAssociationSettings"
              @update:cancel="store.showContactAssociationModal = false"
            />
          </div>
          <div v-else>
            <div class="form-group">
              <div class="flex space-x-3">
                <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.question")
                }}</span>
              </div>
              <UIInput
                id="fieldTitle"
                class="standard-input-ghl"
                :model-value="getLabel"
                :status="customFieldError ? 'error' : undefined"
                @update:model-value="updateLabel"
                @blur="store.addToHistory()"
              >
              </UIInput>
              <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                {{ t("common.customFieldExistsError") }}
              </div>
            </div>
            <div class="form-group">
              <LabelAlignment
                :is-element-level="true"
                :label-alignment="selectedElementStore.labelAlignment"
                @update:label-alignment="handleLabelAlignment"
              ></LabelAlignment>
            </div>
            <div
              v-if="!['TEXTBOX_LIST', 'SIGNATURE'].includes(selectedElementStore?.dataType)"
              class="form-group"
            >
              <div class="flex space-x-3">
                <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.placeholder")
                }}</span>
              </div>
              <UIInput
                id="fieldPlaceholder"
                class="standard-input-ghl"
                :model-value="getPlaceholder"
                @update:model-value="updatePlaceholder"
                @blur="store.addToHistory()"
              ></UIInput>
            </div>
            <div
              v-if="
                !['image', 'header', 'button', 'terms_and_conditions'].includes(
                  selectedElementStore.tag
                )
              "
              class="form-group"
            >
              <div class="flex space-x-3">
                <span class="switch-handle mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.shortLabel")
                }}</span>
              </div>
              <UIInput
                id="shortLabel"
                class="standard-input-ghl"
                :model-value="getShortLabel"
                @update:model-value="updateShortLabel"
                @blur="store.addToHistory()"
              >
              </UIInput>
            </div>
            <div
              v-if="
                !['TEXTBOX_LIST', 'MULTIPLE_OPTIONS', 'FILE_UPLOAD', 'SIGNATURE'].includes(
                  selectedElementStore?.dataType
                )
              "
              class="form-group"
            >
              <div class="flex space-x-3">
                <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                  {{ t("common.queryKey") }}
                  <UITooltip trigger="hover" placement="top">
                    <template #trigger>
                      <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                    </template>
                    {{ t("common.querykeyTooltipMessage") }}
                  </UITooltip>
                </div>
              </div>
              <UIInput
                id="fieldQueryKey"
                class="standard-input-ghl"
                :model-value="getHiddenQuery"
                @update:model-value="updateHiddenQuery"
                @blur="store.addToHistory()"
              ></UIInput>
            </div>
            <div
              v-if="
                ['RADIO', 'CHECKBOX'].includes(selectedElementStore?.dataType) &&
                selectedElementStore?.picklistOptionsImage?.length
              "
              class="m-2 flex flex-col gap-2 self-stretch"
            >
              <Switch
                :value="getSpreadColumns"
                class="w-full"
                :label="t('common.spreadToColumns')"
                @update:value="updateSpreadColumns"
              ></Switch>
              <UIInputGroup v-if="getSpreadColumns">
                <UIInputNumber
                  id="fieldQueryKey"
                  size="large"
                  class="w-100"
                  :min="1"
                  :max="10"
                  :default-value="getDefaultNumberOfColumns"
                  :show-button="false"
                  :model-value="getNumberOfColumns"
                  @update:model-value="updateNumberOfColumns"
                  @blur="store.addToHistory()"
                ></UIInputNumber
                ><UIInputGroupLabel>{{ t("common.columns") }}</UIInputGroupLabel>
              </UIInputGroup>
            </div>
            <div class="form-check-req-hid mb-3">
              <UICheckbox
                id="fieldRequired"
                :disabled="isRequiredCheckboxDisabled"
                :checked="getRequired"
                @update:checked="updateRequired"
                >{{ t("common.required") }}</UICheckbox
              >
              <UICheckbox
                v-if="
                  ![
                    'TEXTBOX_LIST',
                    'SINGLE_OPTIONS',
                    'MULTIPLE_OPTIONS',
                    'FILE_UPLOAD',
                    'SIGNATURE',
                  ].includes(selectedElementStore?.dataType)
                "
                id="fieldCheckbox"
                :disabled="isHiddenFieldDisable"
                :checked="getHidden"
                @update:checked="updateHidden"
                >{{ t("common.hidden") }}</UICheckbox
              >
            </div>
            <div v-if="selectedElementStore.hidden" class="form-group hidden-field">
              <div class="flex space-x-3">
                <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
                  t("common.hiddenValue")
                }}</span>
              </div>
              <UIInput
                id="fieldHiddenvalue"
                class="standard-input-ghl"
                :model-value="getHiddenValue"
                @update:model-value="updateHiddenValue"
                @blur="store.addToHistory()"
              ></UIInput>
            </div>
            <div>
              <UICheckbox
                v-if="
                  selectedElementStore.tag == 'phone' || selectedElementStore.dataType == 'PHONE'
                "
                id="fieldCheckbox"
                :checked="getEnableCountryPicker"
                @update:checked="updateEnableCountryPicker"
                >{{ t("common.enableCountryPicker") }}</UICheckbox
              >
            </div>
            <div v-if="selectedElementStore?.model === 'opportunity'">
              <UIDivider />
              <div class="flex justify-center">
                <UIButton
                  id="edit-opportunity-settings"
                  type="tertiary"
                  @click="store.showOpportunitiesModal = true"
                >
                  <Edit01Icon class="mr-2 h-4 w-4" />
                  {{ t("common.editOpportunitySettings") }}
                </UIButton>
              </div>
              <p v-if="!store.opportunitySettings" class="pt-6">
                <UIAlert id="warning-message" type="warning">
                  <template #content>
                    {{ t("common.opportunitySettingsWarning") }}
                  </template>
                </UIAlert>
              </p>
            </div>
            <div v-if="isTrueCustomObjectField(selectedElementStore?.model)">
              <UIDivider />
              <div class="mb-3 flex justify-center">
                <UIButton
                  id="edit-custom-object-association"
                  type="tertiary"
                  @click="
                    store.currentCustomObjectModel = selectedElementStore?.model;
                    store.showContactAssociationModal = true;
                  "
                >
                  <Edit01Icon class="mr-2 h-4 w-4" />
                  {{
                    t("common.editAssociationButton", {
                      edit: t("common.edit"),
                      objectName: getCustomObjectNamePlural(
                        selectedElementStore?.model,
                        store.customObjects || []
                      ),
                      association: t("common.association"),
                    })
                  }}
                </UIButton>
              </div>
            </div>
            <EditOpportunitySettingsModal
              :show="store.showOpportunitiesModal"
              @update:success="saveOpportunitySettings"
              @update:cancel="store.showOpportunitiesModal = false"
            />
            <ContactAssociationSettingsModal
              :show="store.showContactAssociationModal"
              :object-model="store.currentCustomObjectModel"
              @update:success="saveContactAssociationSettings"
              @update:cancel="store.showContactAssociationModal = false"
            />
            <div v-if="selectedElementStore?.tag === 'email' && !selectedElementStore?.hidden">
              <div class="mt-8 flex justify-between">
                <span class="hl-text-input-label mb-1 block text-gray-700">
                  {{ t("common.validateEmailHeader") }}
                </span>
                <UICheckbox
                  id="email-checkbox"
                  v-model:checked="enableValidateEmailModel"
                  @update:checked="store.addToHistory"
                />
              </div>
              <div class="text-xs text-gray-700">
                {{ t("common.validateEmailDescription") }}
              </div>
            </div>
            <div
              v-if="
                (selectedElementStore?.tag === 'phone' ||
                  selectedElementStore?.dataType === 'PHONE') &&
                !selectedElementStore?.hidden
              "
            >
              <div class="mt-8 flex justify-between">
                <span class="hl-text-input-label mb-1 block text-gray-700">
                  {{ t("common.validatePhoneHeader") }}
                </span>
                <UICheckbox
                  id="phone-checkbox"
                  v-model:checked="enableValidatePhoneModel"
                  @update:checked="store.addToHistory"
                />
              </div>
              <div class="mb-4 text-xs text-gray-700">
                {{ t("common.validatePhoneDescription") }}
              </div>
              <UIAlert id="warning-message" type="warning">
                <template #content>
                  {{ t("common.validatePhoneWarning") }}
                </template>
              </UIAlert>
            </div>
          </div>
        </UICollapseItem>
        <UICollapseItem
          v-if="
            OPTIONS_FIELD.includes(selectedElementStore.dataType) &&
            !selectedElementStore?.picklistOptionsImage?.length
          "
          id="options"
          :title="t('common.options')"
          name="Options"
        >
          <section class="flex flex-col items-start gap-4">
            <section class="flex items-end">
              <section class="flex flex-col items-start gap-1.5 self-stretch">
                <ListEditor
                  v-if="selectedElementStore?.dataType === 'TEXTBOX_LIST'"
                  :key="`list-editor-${slideIndex}-${elementIndex}-${optionModifierKey}-${
                    selectedElementStore?.picklistOptions?.length || 0
                  }`"
                  :picklist-options="selectedElementStore?.picklistOptions"
                  :custom-field-created="
                    selectedElementStore?.customEdited || !selectedElementStore?.custom
                  "
                  :hide-prefill-value="isCustomObjectField(selectedElementStore?.model)"
                  @update:picklist-options="updatePicklistOptions"
                />
                <RadioSelectEditor
                  v-if="selectedElementStore?.dataType === 'RADIO'"
                  :key="`radio-editor-${slideIndex}-${elementIndex}-${optionModifierKey}-${
                    selectedElementStore?.enableCalculations ? 'calc' : 'normal'
                  }-${selectedElementStore?.picklistOptions?.length || 0}`"
                  :is-quiz="store.quizId"
                  :slide-index="slideIndex"
                  :element-index="elementIndex"
                  :selected-element="selectedElementStore"
                  :question-options="questionOptions"
                  :options="selectedElementStore?.picklistOptions"
                  :hide-prefill-value="isCustomObjectField(selectedElementStore?.model)"
                  :custom-field-created="
                    selectedElementStore?.customEdited || !selectedElementStore?.custom
                  "
                  @update:options="updatePicklistOptions"
                  @update:style="updateStyle"
                />
                <DropdownEditor
                  v-if="
                    ['SINGLE_OPTIONS', 'MULTIPLE_OPTIONS', 'CHECKBOX'].includes(
                      selectedElementStore?.dataType
                    )
                  "
                  :key="`dropdown-editor-${slideIndex}-${elementIndex}-${optionModifierKey}-${
                    selectedElementStore?.enableCalculations ? 'calc' : 'normal'
                  }-${selectedElementStore?.picklistOptions?.length || 0}`"
                  :is-quiz="store.quizId"
                  :slide-index="slideIndex"
                  :element-index="elementIndex"
                  :selected-element="selectedElementStore"
                  :question-options="questionOptions"
                  :options="selectedElementStore?.picklistOptions"
                  :hide-prefill-value="isCustomObjectField(selectedElementStore?.model)"
                  :custom-field-created="
                    selectedElementStore?.customEdited || !selectedElementStore?.custom
                  "
                  @update:options="updatePicklistOptions"
                  @update:style="updateStyle"
                />
                <ScoreEditor
                  v-if="selectedElementStore?.dataType === 'SCORE'"
                  :calculation="selectedElementStore?.calculation"
                  :product-type="PRODUCT_TYPE.QUIZ"
                  @update:calculation="updateCalculation"
                />
                <UIAlert
                  v-if="
                    [
                      'TEXTBOX_LIST',
                      'SINGLE_OPTIONS',
                      'MULTIPLE_OPTIONS',
                      'RADIO',
                      'CHECKBOX',
                    ].includes(selectedElementStore?.dataType)
                  "
                  id="warning-message"
                  type="warning"
                >
                  <template #content>
                    {{ t("common.swapWarningMessageForm") }}
                  </template>
                </UIAlert>
                <div
                  v-if="['RADIO', 'CHECKBOX'].includes(selectedElementStore?.dataType)"
                  class="m-2 flex flex-col gap-2 self-stretch"
                >
                  <Switch
                    :value="getSpreadColumns"
                    class="w-full"
                    :label="t('common.spreadToColumns')"
                    @update:value="updateSpreadColumns"
                  ></Switch>
                  <UIInputGroup v-if="getSpreadColumns">
                    <UIInputNumber
                      id="fieldQueryKey"
                      size="large"
                      class="w-100"
                      :min="1"
                      :max="10"
                      :default-value="getDefaultNumberOfColumns"
                      :show-button="false"
                      :model-value="getNumberOfColumns"
                      @update:model-value="updateNumberOfColumns"
                      @blur="store.addToHistory()"
                    ></UIInputNumber
                    ><UIInputGroupLabel>{{ t("common.columns") }}</UIInputGroupLabel>
                  </UIInputGroup>
                </div>
              </section>
            </section>
          </section>
        </UICollapseItem>
        <UICollapseItem
          v-if="selectedElementStore?.dataType === 'FILE_UPLOAD'"
          id="uploadOptions"
          :title="t('common.uploadOptions')"
          name="Upload Options"
        >
          <FileUploadEditor
            :options="selectedElementStore?.picklistOptions || ['all']"
            @update:options="updatePicklistOptions"
          />
          <div>
            <div class="my-4 flex items-center justify-between">
              <div class="my-auto text-sm font-normal text-gray-700">
                {{ t("common.allowMultipleFiles") }}
              </div>
              <UISwitch v-model:value="selectedElementStore.isMultipleFile" size="small" />
            </div>
            <div v-if="selectedElementStore.isMultipleFile">
              <div class="mb-2 mt-4 text-sm font-normal text-gray-700">
                {{ t("common.maxFileLimit") }}
              </div>
              <UIInput id="max-file-limit" v-model:value="selectedElementStore.maxFileSize" />
            </div>
          </div>
        </UICollapseItem>
        <UICollapseItem
          v-if="!selectedElementStore?.standard && selectedElement.elementId !== undefined"
          id="advanced-settings"
          :title="t('common.advancedSettings')"
          name="Advanced Settings"
        >
          <div>
            <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
              {{ t("common.customFieldName") }}
              <UITooltip trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                </template>
                {{ t("common.customFieldNameTooltip") }}
              </UITooltip>
            </div>
            <UIInput
              id="custom-field-label"
              class="standard-input-ghl"
              :model-value="
                selectedElementStore?.customFieldLabel ||
                selectedElementStore?.name ||
                selectedElementStore?.label
              "
              :disabled="disableCustomFieldName"
              :status="customFieldError ? 'error' : undefined"
              @update:model-value="updateLabel"
              @blur="store.addToHistory()"
            />
            <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
              {{ t("common.customFieldExistsError") }}
            </div>
            <div class="switch-handle mb-1 mt-6 block text-sm font-medium text-gray-700">
              {{ t("common.uniqueKey") }}
              <UITooltip trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                </template>
                {{ t("common.uniqueKeyTooltip") }}
              </UITooltip>
            </div>
            <UIInput
              id="uniqueKey"
              class="standard-input-ghl"
              :model-value="getUniqueKey"
              :disabled="disableUniqueKey"
              :placeholder="uniqueKeyPlaceholder"
              :status="uniqueKeyError ? 'error' : undefined"
              @update:model-value="updateUniqueKey"
              @blur="store.addToHistory()"
            />
            <div v-if="uniqueKeyError" class="mt-1 text-xs text-red-500">
              {{ t("common.uniqueKeyNotUniqueError") || "This key is not unique" }}
            </div>
          </div>
        </UICollapseItem>
      </UICollapse>
    </div>
  </section>
</template>

<style>
.back-section {
  display: flex;
  width: 360px;
  padding: 0px 16px;
  align-items: center;
  gap: 8px;
}
#quizSettings {
  display: flex;
  padding: 0px 0px;
  flex-direction: column;
  justify-content: center;
  justify-items: center;
  align-self: stretch;
}
#quizSettings .n-collapse-item__header {
  display: flex;
  align-items: start;
  padding: 16px;
  gap: 20px;
  border-bottom: 1px solid v #eaecf0;
  background: #f9fafb;
}
#quizSettings .n-collapse .n-collapse-item {
  margin-top: 0;
}
#quizSettings .n-collapse-item__content-wrapper {
  padding: 0 16px;
}
.field-name-sidebar {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center !important;
  margin-bottom: 1rem !important;
}

#custom-sidebar-form {
  font-size: 14px;
}

.standard-input-ghl {
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.query-key {
  display: flex;
  align-items: baseline;
}

.form-group {
  margin-bottom: 1rem;
}

.hidden-field {
  margin-top: 1rem;
}

.form-check-req-hid {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.arrow-button {
  margin-bottom: 0px;
  cursor: pointer;
}

.alignment {
  display: flex;
  margin-bottom: 10px;
}

.alignment-div {
  margin-right: 10px;
}

.custom-icons {
  color: #38a0db;
  margin: 0 10px;
  cursor: pointer;
}

#hide-tabs .n-tabs-nav-scroll-wrapper {
  display: none !important;
}

#logic-card > div.p-3 {
  padding-bottom: 1px !important;
}
</style>
